import { ItemData } from './inventory';

export enum RewardType {
	// Отдельные предметы
	VEHICLE = 'vehicle',
	MONEY = 'money',
	EXP = 'exp',
	ITEM = 'item',
	// Кейсы/лутбоксы
	LOOTBOX = 'lootbox',
	// Наборы/Сеты
	BUNDLE = 'bundle',
	// Тюнинг транспорта
	VEHICLE_TUNING_KIT = 'vehicle_tuning_kit',
	VEHICLE_TUNING_ITEM = 'vehicle_tuning_item',
}

export type RewardRaw = { type: RewardType; value: string };

/** ID набора */
export type BundleRewardValue = string;

/** Кол-во опыта */
export type ExpRewardValue = number;

/** Предмет инвентаря  */
export type InventoryItemRewardValue = {
	id: string;
	count?: number;
	data?: ItemData;
};

/** ID кейса  */
export type LootboxRewardValue = string;

/** Кол-во денег */
export type MoneyRewardValue = number;

export type VehicleRewardValue = {
	model: string;
	tuning?: {
		items?: string[];
		kit?: string;
	};
};

export type VehicleTuningKitRewardValue = string;

export type VehicleTuningItemRewardValue = string;

export type ParseRewardValueTypeMap = {
	[RewardType.BUNDLE]: BundleRewardValue;
	[RewardType.EXP]: ExpRewardValue;
	[RewardType.ITEM]: InventoryItemRewardValue;
	[RewardType.LOOTBOX]: LootboxRewardValue;
	[RewardType.MONEY]: MoneyRewardValue;
	[RewardType.VEHICLE]: VehicleRewardValue;
	[RewardType.VEHICLE_TUNING_KIT]: VehicleTuningKitRewardValue;
	[RewardType.VEHICLE_TUNING_ITEM]: VehicleTuningItemRewardValue;
};

export function parseRewardString<T extends RewardType>(type: T, value: string): ParseRewardValueTypeMap[T] {
	switch (type) {
		case RewardType.BUNDLE: {
			return value as ParseRewardValueTypeMap[T];
		}

		case RewardType.EXP: {
			const expAmount = parseInt(value);
			if (expAmount <= 0) {
				throw new Error('Error parsing value of reward. Negative exp value was used');
			}

			return expAmount as ParseRewardValueTypeMap[T];
		}

		case RewardType.ITEM: {
			let isValid = true;

			const itemValue = JSON.parse(value) as unknown;
			if (typeof itemValue !== 'object' || itemValue === null) {
				throw new Error('Error parsing value of reward. Not an object');
			}

			if (typeof (itemValue as any).id !== 'string') {
				isValid = false;
			}

			if (
				(itemValue as any).count !== undefined &&
				(typeof (itemValue as any).count !== 'number' || (itemValue as any).count <= 0)
			) {
				isValid = false;
			}

			if (
				((itemValue as any).data !== undefined && typeof (itemValue as any).data !== 'object') ||
				((itemValue as any).data !== undefined && (itemValue as any).data === null)
			) {
				isValid = false;
			}

			if (!isValid) {
				throw new Error(`Error parsing value of reward. Value is invalid`);
			}

			return itemValue as ParseRewardValueTypeMap[T];
		}

		case RewardType.LOOTBOX: {
			return value as ParseRewardValueTypeMap[T];
		}

		case RewardType.MONEY: {
			const moneyValue = Number(value);

			if (isNaN(moneyValue) || value.trim() === '') {
				throw new Error('Error parsing reward. Not a number');
			}

			return moneyValue as ParseRewardValueTypeMap[T];
		}

		case RewardType.VEHICLE: {
			let isValid = true;

			const vehicleValue = JSON.parse(value) as unknown;
			if (typeof vehicleValue !== 'object' || vehicleValue === null) {
				throw new Error('Error parsing value of reward. Not an object');
			}

			if (typeof (vehicleValue as any).model !== 'string') {
				isValid = false;
			}

			if ((vehicleValue as any).tuning) {
				const items: unknown = (vehicleValue as any).tuning.items;
				if (items !== undefined && (!Array.isArray(items) || !items.every((e) => typeof e === 'string'))) {
					isValid = false;
				}

				const kit: unknown = (vehicleValue as any).tuning.kit;
				if (kit !== undefined && typeof kit !== 'string') {
					isValid = false;
				}
			}

			if (!isValid) {
				throw new Error('Error parsing value of reward. Value is invalid');
			}

			return vehicleValue as ParseRewardValueTypeMap[T];
		}

		case RewardType.VEHICLE_TUNING_KIT: {
			if (!value || typeof value !== 'string') {
				throw new Error('Error parsing value of reward. Kit ID must be a string');
			}

			return value as ParseRewardValueTypeMap[T];
		}

		case RewardType.VEHICLE_TUNING_ITEM: {
			if (!value || typeof value !== 'string') {
				throw new Error('Error parsing value of reward. Item ID must be a string');
			}

			return value as ParseRewardValueTypeMap[T];
		}

		default: {
			throw new Error('No value parsing for reward');
		}
	}
}
